import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createLabCase,
  createLabWatchlistItem,
  getLabCockpit,
  getLabExecutiveSummary,
  getLabFusionStatus,
  markLabReviewedItem,
  runLabFusion,
} from './api/smartiLabApi';
import LabCard from './components/LabCard';
import LabEmptyState from './components/LabEmptyState';
import LabMetric from './components/LabMetric';
import LabTable from './components/LabTable';
import SmartiLabBadge from './components/SmartiLabBadge';
import SmartiLabRunOverlay from './components/SmartiLabRunOverlay';
import { useSmartiLabContext } from './SmartiLabContext';

export default function SmartiLabHome() {
  const navigate = useNavigate();
  const { sharedQuery, isAdmin, refreshTrustSnapshot } = useSmartiLabContext();
  const [cockpit, setCockpit] = useState(null);
  const [executive, setExecutive] = useState(null);
  const [busyKey, setBusyKey] = useState('');
  const [runOverlay, setRunOverlay] = useState({ open: false, stage: 'starting', startedAt: '', errorMessage: '', errorTitle: '', errorDetails: '', recoverySteps: [], phaseKey: '', progressValue: null });
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [cockpitResult, executiveResult] = await Promise.all([
      getLabCockpit(sharedQuery),
      getLabExecutiveSummary(sharedQuery),
    ]);
    setCockpit(cockpitResult);
    setExecutive(executiveResult);
  }, [sharedQuery]);

  useEffect(() => {
    load().catch((loadError) => setError(loadError.message || 'Unable to load Smarti Lab cockpit.'));
  }, [load]);

  const refreshAll = useCallback(async () => {
    await Promise.all([load(), refreshTrustSnapshot?.()]);
  }, [load, refreshTrustSnapshot]);

  const handleOpenCase = async (payload, successMessageKey) => {
    setBusyKey(successMessageKey);
    setError('');
    try {
      await createLabCase(payload);
      await refreshAll();
    } catch (actionError) {
      setError(actionError.message || 'Could not create a case.');
    } finally {
      setBusyKey('');
    }
  };

  const handleWatch = async (payload, key) => {
    setBusyKey(key);
    setError('');
    try {
      await createLabWatchlistItem(payload);
      await refreshAll();
    } catch (actionError) {
      setError(actionError.message || 'Could not add this item to the watchlist.');
    } finally {
      setBusyKey('');
    }
  };

  const handleReviewed = async (payload, key) => {
    setBusyKey(key);
    setError('');
    try {
      await markLabReviewedItem(payload);
      await refreshAll();
    } catch (actionError) {
      setError(actionError.message || 'Could not mark this item as reviewed.');
    } finally {
      setBusyKey('');
    }
  };

  const handleRunFusion = async () => {
    setBusyKey('run-fusion');
    setRunOverlay({ open: true, stage: 'starting', startedAt: '', errorMessage: '', errorTitle: '', errorDetails: '', recoverySteps: [], phaseKey: '', progressValue: 3 });
    try {
      const result = await runLabFusion();
      setRunOverlay({
        open: true,
        stage: 'running',
        startedAt: result?.runState?.lastStartedAt || new Date().toISOString(),
        errorMessage: '',
        errorTitle: '',
        errorDetails: '',
        recoverySteps: [],
        phaseKey: result?.runState?.phaseKey || '',
        progressValue: result?.runState?.progressPercent ?? 8,
      });
      let finished = false;
      while (!finished) {
        // poll until the background ML run reaches a terminal state
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => window.setTimeout(resolve, 1200));
        // eslint-disable-next-line no-await-in-loop
        const status = await getLabFusionStatus();
        if (status?.running) {
          setRunOverlay((current) => ({
            ...current,
            open: true,
            stage: 'running',
            startedAt: status?.lastStartedAt || current.startedAt,
            phaseKey: status?.phaseKey || current.phaseKey,
            progressValue: status?.progressPercent ?? current.progressValue,
          }));
          continue;
        }

        finished = true;
        await refreshAll();
        if (status?.lastStatus === 'success') {
          setRunOverlay({
            open: true,
            stage: 'success',
            startedAt: status?.lastStartedAt || '',
            errorMessage: '',
            errorTitle: '',
            errorDetails: '',
            recoverySteps: [],
            phaseKey: 'completed',
            progressValue: 100,
          });
        } else {
          setRunOverlay({
            open: true,
            stage: 'error',
            startedAt: status?.lastStartedAt || '',
            errorMessage: status?.lastError || 'The fusion pipeline finished with an error.',
            errorTitle: 'Fusion pipeline failed',
            errorDetails: status?.lastError || '',
            recoverySteps: ['Check the Smarti Lab warnings.', 'Open the ops console for technical detail.', 'Retry the run after the blocker is resolved.'],
            phaseKey: status?.phaseKey || '',
            progressValue: status?.progressPercent ?? 0,
          });
        }
      }
    } catch (runError) {
      setRunOverlay({
        open: true,
        stage: 'error',
        startedAt: '',
        errorMessage: runError.message || 'Could not start the fusion pipeline.',
        errorTitle: 'Fusion pipeline failed',
        errorDetails: runError.message || '',
        recoverySteps: ['Check the Smarti Lab warnings.', 'Verify Python/ML dependencies.', 'Retry the run after the data blockers are resolved.'],
        phaseKey: '',
        progressValue: 0,
      });
    } finally {
      setBusyKey('');
    }
  };

  const blocks = [
    {
      key: 'sessions',
      title: 'Top risky sessions',
      rows: cockpit?.topRiskySessions || [],
      render: (row) => {
        const key = `session:${row.global_id}`;
        return (
          <article key={key} className="lab-suspect-card">
            <div className="lab-chip-row">
              <SmartiLabBadge variant="danger" label={`Risk ${(Number(row.rf_failure_probability_calibrated ?? row.rf_failure_probability ?? 0) * 100).toFixed(0)}%`} />
              <SmartiLabBadge variant="neutral" label={row.serialnumber || 'Unknown serial'} />
            </div>
            <strong>Session #{row.global_id}</strong>
            <p>{row.station_name_norm || row.first_fail_step_norm || 'Session investigation priority'}</p>
            <div className="lab-action-row">
              <button className="lab-inline-button" type="button" onClick={() => navigate(`/smarti-lab/compare?mode=session&left=${encodeURIComponent(row.global_id)}`)}>Compare to previous run</button>
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:case`} onClick={() => handleOpenCase({
                title: `Review risky session ${row.global_id}`,
                priority: 'high',
                status: 'open',
                linked_entity_type: 'session',
                linked_entity_key: String(row.global_id),
                linked_station_name: row.station_name_norm || '',
                linked_testspec: row.testspec_norm || '',
                notes: `RF risk ${(Number(row.rf_failure_probability_calibrated ?? row.rf_failure_probability ?? 0) * 100).toFixed(1)}%.`,
              }, `${key}:case`)}>Open case</button>
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:watch`} onClick={() => handleWatch({
                entity_type: 'session',
                entity_key: String(row.global_id),
                label: `Session ${row.global_id}`,
                note: row.station_name_norm || '',
                severity: 'high',
              }, `${key}:watch`)}>Watch</button>
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:review`} onClick={() => handleReviewed({
                itemType: 'session',
                itemKey: String(row.global_id),
                label: `Session ${row.global_id}`,
              }, `${key}:review`)}>{row.reviewed ? 'Reviewed' : 'Mark as reviewed'}</button>
            </div>
          </article>
        );
      },
    },
    {
      key: 'suspects',
      title: 'Top recurring suspects',
      rows: cockpit?.topRecurringSuspects || [],
      render: (row) => {
        const key = `suspect:${row.candidate_id}`;
        return (
          <article key={key} className="lab-suspect-card">
            <div className="lab-chip-row">
              <SmartiLabBadge variant="primary" label={`Priority ${Number(row.investigation_priority || 0).toFixed(2)}`} />
              <SmartiLabBadge variant="success" label={`Confidence ${Number(row.suspect_confidence || 0).toFixed(2)}`} />
            </div>
            <strong>{row.stepname || row.stepnumber || 'Unnamed suspect'}</strong>
            <p>{[row.station_name_norm, row.testspec_norm, row.machine_identifier_norm].filter(Boolean).join(' • ') || 'Evidence-first suspect workbench'}</p>
            <div className="lab-action-row">
              <Link className="lab-inline-button" to={`/smarti-lab/suspects/${encodeURIComponent(row.candidate_id)}`}>Open evidence page</Link>
              <button className="lab-inline-button" type="button" onClick={() => navigate(`/smarti-lab/compare?mode=suspect&left=${encodeURIComponent(row.candidate_id)}`)}>Compare to previous run</button>
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:case`} onClick={() => handleOpenCase({
                title: `Investigate suspect ${row.stepname || row.stepnumber}`,
                priority: 'high',
                status: 'investigating',
                suspected_root_cause: row.stepname || row.stepnumber,
                linked_entity_type: 'suspect',
                linked_entity_key: String(row.candidate_id),
                linked_step_name: row.stepname || '',
                linked_station_name: row.station_name_norm || '',
                linked_ate_version: row.ate_version_norm || '',
                linked_testspec: row.testspec_norm || '',
                notes: `Investigation priority ${Number(row.investigation_priority || 0).toFixed(3)}.`,
              }, `${key}:case`)}>Open case</button>
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:watch`} onClick={() => handleWatch({
                entity_type: 'suspect',
                entity_key: String(row.candidate_id),
                label: row.stepname || row.stepnumber,
                note: row.station_name_norm || '',
                severity: 'high',
              }, `${key}:watch`)}>Watch</button>
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:review`} onClick={() => handleReviewed({
                itemType: 'suspect',
                itemKey: String(row.candidate_id),
                label: row.stepname || row.stepnumber,
              }, `${key}:review`)}>{row.reviewed ? 'Reviewed' : 'Mark as reviewed'}</button>
            </div>
          </article>
        );
      },
    },
    {
      key: 'drift',
      title: 'Active drift alerts',
      rows: cockpit?.activeDriftAlerts || [],
      render: (row) => {
        const itemKey = row.alert_id || `${row.scope_type}:${row.scope_value}:${row.step_name_norm}`;
        const key = `drift:${itemKey}`;
        return (
          <article key={key} className="lab-suspect-card">
            <div className="lab-chip-row">
              <SmartiLabBadge variant="warning" label={`Severity ${Number(row.severity_score || 0).toFixed(2)}`} />
              <SmartiLabBadge variant="neutral" label={row.scope_type || 'drift'} />
            </div>
            <strong>{row.scope_value || row.step_name_norm || 'Drift alert'}</strong>
            <p>{row.message || row.signal_label || 'This area changed materially versus the previous window.'}</p>
            <div className="lab-action-row">
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:case`} onClick={() => handleOpenCase({
                title: `Check drift alert ${row.scope_value || row.step_name_norm}`,
                priority: 'medium',
                status: 'open',
                linked_entity_type: 'drift',
                linked_entity_key: String(itemKey),
                linked_step_name: row.step_name_norm || '',
                linked_station_name: row.scope_value || '',
                notes: row.message || 'Review drift alert',
              }, `${key}:case`)}>Open case</button>
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:review`} onClick={() => handleReviewed({
                itemType: 'drift',
                itemKey: String(itemKey),
                label: row.scope_value || row.step_name_norm,
              }, `${key}:review`)}>{row.reviewed ? 'Reviewed' : 'Mark as reviewed'}</button>
            </div>
          </article>
        );
      },
    },
    {
      key: 'quality',
      title: 'Data-quality blockers',
      rows: cockpit?.dataQualityBlockers || [],
      render: (row) => {
        const key = `quality:${row.warning_type || row.message}`;
        return (
          <article key={key} className="lab-suspect-card">
            <div className="lab-chip-row">
              <SmartiLabBadge variant="danger" label={row.severity || 'warning'} />
              <SmartiLabBadge variant="neutral" label={row.warning_type || 'data quality'} />
            </div>
            <strong>{row.message || 'Data quality blocker'}</strong>
            <p>{row.recovery_hint || row.scope_value || 'This blocker should be resolved before trusting the output.'}</p>
            <div className="lab-action-row">
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:case`} onClick={() => handleOpenCase({
                title: `Resolve data blocker ${row.warning_type || 'quality'}`,
                priority: 'critical',
                status: 'open',
                linked_entity_type: 'quality',
                linked_entity_key: String(row.warning_type || row.message),
                notes: row.message || '',
              }, `${key}:case`)}>Open case</button>
              <button className="lab-inline-button" type="button" disabled={busyKey === `${key}:review`} onClick={() => handleReviewed({
                itemType: 'quality',
                itemKey: String(row.warning_type || row.message),
                label: row.warning_type || 'Quality blocker',
              }, `${key}:review`)}>{row.reviewed ? 'Reviewed' : 'Mark as reviewed'}</button>
            </div>
          </article>
        );
      },
    },
  ];

  return (
    <div className="lab-page">
      <section className="lab-page-hero">
        <div className="lab-page-hero-layout">
          <div className="lab-page-hero-meta">
            <div className="lab-page-hero-head">
              <span className="lab-badge">Command center</span>
              <h2>What should I do now?</h2>
            </div>
            <p className="lab-page-hero-subtitle">
              {executive?.executiveMessage || 'Smarti Lab now starts with an investigation cockpit: risky sessions, recurring suspects, drift alerts, and data blockers, all with direct next actions.'}
            </p>
          </div>
          <div className="lab-page-hero-panel">
            <div className="lab-hero-highlights">
              <div className="lab-hero-highlight">
                <strong>Open actions</strong>
                <p>{executive?.openActionCount ?? 0}</p>
              </div>
              <div className="lab-hero-highlight">
                <strong>Drift alerts</strong>
                <p>{cockpit?.activeDriftAlerts?.length ?? 0}</p>
              </div>
              <div className="lab-hero-highlight">
                <strong>Quality blockers</strong>
                <p>{cockpit?.dataQualityBlockers?.length ?? 0}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {error ? <div className="lab-error-text">{error}</div> : null}

      <div className="lab-metric-grid">
        <LabMetric label="Risky sessions" value={cockpit?.topRiskySessions?.length ?? 0} />
        <LabMetric label="Recurring suspects" value={cockpit?.topRecurringSuspects?.length ?? 0} />
        <LabMetric label="Active cases" value={cockpit?.openCases?.length ?? 0} />
        <LabMetric label="Watchlist items" value={cockpit?.watchlist?.length ?? 0} />
      </div>

      <div className="lab-cockpit-grid">
        {blocks.map((block) => (
          <section key={block.key} className="lab-cockpit-column">
            <h3>{block.title}</h3>
            {block.rows.length ? block.rows.map(block.render) : <LabEmptyState title={`No ${block.title.toLowerCase()} right now`} description="Nothing in this block needs immediate action under the current scope." />}
          </section>
        ))}
      </div>

      <div className="lab-grid two-col">
        <LabCard
          title="Top open investigations"
          description="Cases now close the loop: owner, due date, resolution path, corrective action, and verification result."
          actions={<Link className="lab-button" to="/smarti-lab/cases">Open case workflow</Link>}
        >
          <LabTable
            rows={cockpit?.openCases || []}
            emptyTitle="No open investigations"
            emptyDescription="Create a case from a suspect, session, drift alert, or data blocker to start tracking resolution."
            columns={[
              { key: 'title', label: 'Case' },
              { key: 'status', label: 'Status' },
              { key: 'priority', label: 'Priority' },
              { key: 'assigned_to_username', label: 'Owner' },
              { key: 'due_at', label: 'Due' },
              { key: 'verification_result', label: 'Verification' },
            ]}
          />
        </LabCard>

        <LabCard
          title="Executive layer"
          description="A simpler view for non-technical readers: what changed, what likely drives it, and where to focus next."
          actions={<Link className="lab-button" to="/smarti-lab/executive">Open executive summary</Link>}
        >
          {executive ? (
            <div className="lab-data-stack">
              <strong>{executive.topPriority?.title_en || 'No top executive priority yet'}</strong>
              <p>{executive.executiveMessage}</p>
              <p>Attention stations: {(executive.attentionStations || []).filter(Boolean).join(', ') || 'none'}</p>
            </div>
          ) : (
            <LabEmptyState title="Executive summary unavailable" description="Run Smarti Lab and refresh once the fusion pipeline finishes." />
          )}
        </LabCard>
      </div>

      {isAdmin ? (
        <LabCard
          title="Fusion ops console"
          description="Run the pipeline, inspect current run status, and keep the artifact layer visible to admins."
          actions={<button className="lab-button" type="button" disabled={busyKey === 'run-fusion'} onClick={handleRunFusion}>{busyKey === 'run-fusion' ? 'Starting...' : 'Run now'}</button>}
        >
          <div className="lab-metric-grid">
            <LabMetric label="Pipeline status" value={cockpit?.trust?.available ? 'Ready' : 'Unavailable'} />
            <LabMetric label="Confidence level" value={cockpit?.trust?.confidenceLevel || 'unknown'} />
            <LabMetric label="Warnings" value={cockpit?.trust?.warningCount ?? 0} />
            <LabMetric label="Human feedback" value={cockpit?.trust?.feedbackCount ?? 0} />
          </div>
          <div className="lab-action-row">
            <Link className="lab-inline-button" to="/smarti-lab/ops">Open full ops page</Link>
            <Link className="lab-inline-button" to="/smarti-lab/compare">Open compare mode</Link>
            <Link className="lab-inline-button" to="/smarti-lab/cross-dataset-links">Open link workbench</Link>
          </div>
        </LabCard>
      ) : null}

      <SmartiLabRunOverlay
        open={runOverlay.open}
        stage={runOverlay.stage}
        startedAt={runOverlay.startedAt}
        errorMessage={runOverlay.errorMessage}
        errorTitle={runOverlay.errorTitle}
        errorDetails={runOverlay.errorDetails}
        recoverySteps={runOverlay.recoverySteps}
        phaseKey={runOverlay.phaseKey}
        progressValue={runOverlay.progressValue}
        onClose={() => setRunOverlay((current) => ({ ...current, open: false }))}
      />
    </div>
  );
}
